import { useCallback, useEffect, useRef, useState } from "react";

export { useSmartTimer };

const DEFAULT_IDLE_TIMEOUT_MS = 180000; // 3 minutes
const VITE_IDLE_TIMEOUT = import.meta.env.VITE_LOCK_IDLE_TIMEOUT_MS
  ? Number(import.meta.env.VITE_LOCK_IDLE_TIMEOUT_MS)
  : DEFAULT_IDLE_TIMEOUT_MS;
const IDLE_TIMEOUT_MS = isNaN(VITE_IDLE_TIMEOUT)
  ? DEFAULT_IDLE_TIMEOUT_MS
  : VITE_IDLE_TIMEOUT;
const DEBUG = import.meta.env.VITE_DEBUG === "true";

function useSmartTimer<T, D extends any[]>(
  asyncMethod: (...args: D) => Promise<T | undefined>,
  intervalMs: number,
  callback: (value: T) => void,
  deps: D,
) {
  const timeoutRef = useRef<undefined | number | NodeJS.Timeout>(-1);
  const idleTimeoutRef = useRef<undefined | number | NodeJS.Timeout>(-1);

  const [lastUpdated, setLastUpdated] = useState<null | number>(null);

  // Keep latest refs for callbacks to avoid re-triggering timers on change of handlers/dependencies
  const asyncMethodRef = useRef(asyncMethod);
  const callbackRef = useRef(callback);
  const depsRef = useRef(deps);

  useEffect(() => {
    asyncMethodRef.current = asyncMethod;
    callbackRef.current = callback;
    depsRef.current = deps;
  }, [asyncMethod, callback, deps]);

  const isVisibleRef = useRef(true);
  const isIdleRef = useRef(false);
  const isPollingRef = useRef(false);
  const isMountedRef = useRef(true);
  const instanceIdRef = useRef(Math.random().toString(36).substring(7));

  useEffect(() => {
    if (DEBUG) {
      console.log(
        `[useSmartTimer:${instanceIdRef.current}] hook instance mounted.`,
      );
    }
    return () => {
      if (DEBUG) {
        console.log(
          `[useSmartTimer:${instanceIdRef.current}] hook instance unmounted.`,
        );
      }
      isMountedRef.current = false;
    };
  }, []);

  // Define the function that runs a single poll and schedules the next one
  const poll = useCallback(async () => {
    // Clear any existing poll timeout
    if (timeoutRef.current !== undefined && timeoutRef.current !== -1) {
      clearTimeout(timeoutRef.current as any);
      timeoutRef.current = undefined;
    }

    if (isPollingRef.current) {
      if (DEBUG) {
        console.log(
          `[useSmartTimer:${instanceIdRef.current}] poll ignored (already in-progress).`,
        );
      }
      return;
    }

    if (!isVisibleRef.current || isIdleRef.current) {
      if (DEBUG) {
        console.log(
          `[useSmartTimer:${instanceIdRef.current}] poll skipped (hidden or idle).`,
        );
      }
      return;
    }

    if (DEBUG) {
      console.log(
        `[useSmartTimer:${instanceIdRef.current}] starting poll API request.`,
      );
    }
    isPollingRef.current = true;
    try {
      const asyncResponse = await asyncMethodRef.current(...depsRef.current);

      if (!isMountedRef.current) {
        if (DEBUG) {
          console.log(
            `[useSmartTimer:${instanceIdRef.current}] poll completed after unmount, ignoring result.`,
          );
        }
        return;
      }

      // Double check in case state changed during async execution
      if (!isVisibleRef.current || isIdleRef.current) {
        return;
      }

      if (asyncResponse !== undefined) {
        callbackRef.current(asyncResponse);
        setLastUpdated(Date.now());
      }
    } catch (err) {
      console.error(
        `[useSmartTimer:${instanceIdRef.current}] Error during poll:`,
        err,
      );
    } finally {
      isPollingRef.current = false;
    }

    // Schedule next poll
    if (isMountedRef.current && isVisibleRef.current && !isIdleRef.current) {
      if (DEBUG) {
        console.log(
          `[useSmartTimer:${instanceIdRef.current}] scheduling next poll in ${intervalMs}ms.`,
        );
      }
      timeoutRef.current = setTimeout(poll, intervalMs);
    }
  }, [intervalMs]);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === "visible";
      isVisibleRef.current = isVisible;
      if (isVisible && !isIdleRef.current) {
        // Just returned to the tab and not idle, trigger a poll immediately and resume scheduling
        poll();
      } else {
        // Hidden, clear timeout
        if (timeoutRef.current !== undefined && timeoutRef.current !== -1) {
          clearTimeout(timeoutRef.current as any);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [poll]);

  // Handle user activity / idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (
        idleTimeoutRef.current !== undefined &&
        idleTimeoutRef.current !== -1
      ) {
        clearTimeout(idleTimeoutRef.current as any);
      }

      if (isIdleRef.current) {
        // Was idle, now active again
        isIdleRef.current = false;
        if (isVisibleRef.current) {
          // Immediately trigger poll and resume scheduling
          poll();
        }
      }

      idleTimeoutRef.current = setTimeout(() => {
        isIdleRef.current = true;
        // Idle limit reached, clear current polling timeout
        if (timeoutRef.current !== undefined && timeoutRef.current !== -1) {
          clearTimeout(timeoutRef.current as any);
        }
      }, IDLE_TIMEOUT_MS);
    };

    // Initialize the idle timer on mount
    resetIdleTimer();

    // Listen to user interactions
    const events = [
      "mousemove",
      "mousedown",
      "keypress",
      "touchstart",
      "scroll",
    ];
    events.forEach((event) => {
      window.addEventListener(event, resetIdleTimer);
    });

    return () => {
      if (
        idleTimeoutRef.current !== undefined &&
        idleTimeoutRef.current !== -1
      ) {
        clearTimeout(idleTimeoutRef.current as any);
      }
      events.forEach((event) => {
        window.removeEventListener(event, resetIdleTimer);
      });
    };
  }, [poll]);

  // Initial poll on mount
  useEffect(() => {
    poll();
    return () => {
      if (timeoutRef.current !== undefined && timeoutRef.current !== -1) {
        clearTimeout(timeoutRef.current as any);
        timeoutRef.current = undefined;
      }
    };
  }, [poll]);

  return [lastUpdated];
}
